import { observer } from 'mobx-react-lite';
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { Icons } from '@/assets/icons/icons';
import { Button, type ButtonProps } from '@/components/ui/Button';
import { Command } from '@/components/ui/cmdk/cmdk';
import { XStack } from '@/components/ui/layout/layout';
import { Popover } from '@/components/ui/popover';
import {
  AgentPickerRowItem,
  buildAgentPickerBlocks,
  matchesMode,
  queryTokens,
  toggleMode,
  useAgentPickerQuery,
  type AgentPickerRow,
} from '@/features/agent-picker';
import { chatStore } from '@/features/chat/chat.store';
import { cn } from '@/lib/utils';
import { userStore } from '@/stores/user.store';

type ChatModeContextType = {
  selectedModes: string[];
  onSelect: (row: AgentPickerRow) => void;
  searchQuery: string;
  onSearch: (query: string) => void;
  showOlder: boolean;
  onToggleOlder: () => void;
};

const ChatModeContext = createContext<ChatModeContextType>({
  selectedModes: [],
  onSelect: () => {},
  searchQuery: '',
  onSearch: () => {},
  showOlder: false,
  onToggleOlder: () => {},
});

const useChatMode = () => {
  const context = useContext(ChatModeContext);
  if (!context) {
    throw new Error('useChatMode must be used within a ChatModeProvider');
  }
  return context;
};

type ChatModeDropdownRootProps = {
  children: React.ReactNode;
  selectedModes: string[];
  setSelectedModes: (modes: string[]) => void;
  multiple?: boolean;
};
export const ChatModeDropdownRoot = ({
  children,
  multiple = false,
  selectedModes,
  setSelectedModes,
}: ChatModeDropdownRootProps) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showOlder, setShowOlder] = useState(false);

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      setSearchQuery('');
      setShowOlder(false);
    }
  };

  const handleSelect = (row: AgentPickerRow) => {
    if (!multiple) {
      setSelectedModes([row.mode]);
      handleOpenChange(false);
      return;
    }
    setSelectedModes(toggleMode(selectedModes, row));
  };

  return (
    <ChatModeContext.Provider
      value={{
        selectedModes,
        onSelect: handleSelect,
        searchQuery,
        onSearch: setSearchQuery,
        showOlder,
        onToggleOlder: () => setShowOlder((isShown) => !isShown),
      }}
    >
      <Popover.Root open={open} onOpenChange={handleOpenChange}>
        {children}
      </Popover.Root>
    </ChatModeContext.Provider>
  );
};

type ChatModeDropdownTriggerProps = {} & ButtonProps;
export const ChatModeDropdownTrigger = React.forwardRef<
  HTMLButtonElement,
  ChatModeDropdownTriggerProps
>(({ children, ...props }, ref) => {
  const { selectedModes } = useChatMode();

  const selectedModesStr = () => {
    if (selectedModes.length === 0) return 'Select a mode';
    if (selectedModes.length === 1) return '1 mode selected';
    return `${selectedModes.length} modes selected`;
  };

  return (
    <Popover.Trigger asChild>
      <Button
        size="dense"
        variant="secondary"
        className="gap-2 text-xs"
        ref={ref}
        {...props}
      >
        {children ? (
          children
        ) : (
          <>
            {selectedModesStr()}
            <Icons.chevronDown className="size-2" />
          </>
        )}
      </Button>
    </Popover.Trigger>
  );
});

const GroupHeading = ({ label, count }: { label: string; count: number }) => (
  <XStack className="items-baseline gap-1.5">
    {label}
    <span className="tabular-nums">{count}</span>
  </XStack>
);

type ChatModeDropdownContentProps = {
  container?: HTMLElement | null;
} & Popover.ContentProps;
export const ChatModeDropdownContent = observer(
  function ChatModeDropdownContent({
    className,
    ...props
  }: ChatModeDropdownContentProps) {
    const {
      searchQuery,
      onSearch,
      onSelect,
      selectedModes,
      showOlder,
      onToggleOlder,
    } = useChatMode();
    const { data: entries, isLoading } = useAgentPickerQuery();
    const listRef = useRef<HTMLDivElement>(null);
    const tokens = queryTokens(searchQuery);
    const blocks = buildAgentPickerBlocks({
      entries: entries ?? [],
      allowedModes: userStore.user?.allowed_chat_modes ?? [],
      recentModes: chatStore.recentChatModes,
      query: searchQuery,
      showOlder,
    });

    useEffect(() => {
      listRef.current?.scrollTo({ top: 0, behavior: 'instant' });
    }, [searchQuery]);

    return (
      <Popover.Content
        className={cn('mt-1 w-[28rem] max-w-[calc(100vw-2rem)] p-0', className)}
        {...props}
      >
        <Command.Root shouldFilter={false} loop>
          <Command.Input
            placeholder="Search by model, version, provider…"
            value={searchQuery}
            onValueChange={onSearch}
          />
          <Command.List
            containerRef={listRef}
            containerClassName="max-h-[30rem]"
          >
            {isLoading ? (
              <Command.Loading>Loading agents…</Command.Loading>
            ) : (
              <>
                <Command.Empty>No agents match</Command.Empty>
                {blocks.map((block) =>
                  block.kind === 'group' ? (
                    <Command.Group
                      key={block.id}
                      heading={
                        <GroupHeading
                          label={block.label}
                          count={block.rows.length}
                        />
                      }
                    >
                      {block.rows.map((row) => (
                        <AgentPickerRowItem
                          key={row.mode}
                          row={row}
                          tokens={tokens}
                          selected={selectedModes.some((mode) =>
                            matchesMode(row, mode)
                          )}
                          onSelect={() => onSelect(row)}
                        />
                      ))}
                    </Command.Group>
                  ) : (
                    <Command.Group key="older-toggle">
                      <Command.Item
                        value="older-versions-toggle"
                        onSelect={onToggleOlder}
                        className="text-foreground-subtle"
                      >
                        <Icons.chevronDown
                          className={cn(
                            'size-3',
                            block.expanded && 'rotate-180'
                          )}
                        />
                        <span className="flex-1">
                          {block.expanded
                            ? 'Hide older versions'
                            : 'Older versions'}
                        </span>
                        <span className="tabular-nums text-foreground-muted">
                          {block.count}
                        </span>
                      </Command.Item>
                    </Command.Group>
                  )
                )}
              </>
            )}
          </Command.List>
        </Command.Root>
      </Popover.Content>
    );
  }
);
